import { useCallback, useEffect, useRef, useState } from 'react';
import { Product } from '@interfaces/models/product';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { getProductTrackingData } from '@helpers/utils/catalog/catalog-tracking-utils';

interface UseProductSliderImpressionTrackingProps {
  productFeedCategory: string;
  trackingPayload?: object;
}

const useProductSliderImpressionTracking = ({
  productFeedCategory,
  trackingPayload,
}: UseProductSliderImpressionTrackingProps) => {
  const latestSeenProductIndexRef = useRef<null | number>(null);
  const [seenProducts, setSeenProducts] = useState<Product[]>([]);
  const [trackedProductsIds, setTrackedProductsIds] = useState<string[]>([]);
  const isRevealedRef = useRef(false);
  const { sendAnalyticEvent } = useAnalyticEvents('product');

  const onVisibilityChange = useCallback(
    ({ isVisible, index, products }: { isVisible: boolean; index: number; products: Product[] }) => {
      if (isVisible && (latestSeenProductIndexRef.current === null || index > latestSeenProductIndexRef.current)) {
        latestSeenProductIndexRef.current = index;
        setSeenProducts(products.slice(0, index + 1));
      }
    },
    [],
  );

  const getSliderProductData = useCallback(
    (trackedProduct: Product, index: number) => ({
      ...getProductTrackingData(trackedProduct),
      product_position: String(index + 1),
      product_feed_category: productFeedCategory,
    }),
    [productFeedCategory],
  );

  const onProductClick = useCallback(
    async (clickedProduct: Product, index: number) => {
      sendAnalyticEvent('product_click', {
        ...trackingPayload,
        product_card_details: getSliderProductData(clickedProduct, index),
      });
    },
    [trackingPayload, getSliderProductData],
  );

  const onProductImpression = useCallback(
    (productImpressions: Product[], lastTrackedLength?: number) =>
      sendAnalyticEvent('product_impression', {
        ...trackingPayload,
        product_card_details: productImpressions.map((product, index) => ({
          ...getSliderProductData(product, index),
          product_position: lastTrackedLength !== undefined ? String(lastTrackedLength + index + 1) : String(index + 1),
        })),
      }),
    [getSliderProductData, trackingPayload],
  );

  // product_impression event sent when the product slider is first moved in viewport
  const triggerInitialRevealTracking = useCallback(() => {
    if (!isRevealedRef.current) {
      isRevealedRef.current = true;
      if (seenProducts?.length) {
        onProductImpression(seenProducts);
        setTrackedProductsIds([...trackedProductsIds, ...seenProducts.map((product) => product.id)]);
      }
    }
  }, [onProductImpression, seenProducts]);

  // product_impression event sent when user is scrolling through the product slider
  useEffect(() => {
    if (isRevealedRef.current) {
      const callback = () => {
        // track only the products that have not been tracked yet
        const productsToTrack = seenProducts.filter((product) => !trackedProductsIds.includes(product.id));
        onProductImpression(productsToTrack, trackedProductsIds.length);
        setTrackedProductsIds([...trackedProductsIds, ...productsToTrack.map((product) => product.id)]);
      };
      // using requestIdleCallback to avoid blocking the main thread when user is scrolling through the product slider
      window.requestIdleCallback(callback);
    }
  }, [seenProducts]);

  return { onVisibilityChange, onProductClick, triggerInitialRevealTracking };
};

export default useProductSliderImpressionTracking;
